import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Tag,
  Modal,
  Form,
  Select,
  Space,
  message,
  Typography,
} from "antd";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchDeliveryUsers } from "../deliveryThunks";
import { selectDeliveryUsers, selectDeliveryUsersLoading } from "../deliverySelectors";
import { addDeliveryUser, removeDeliveryUser, getBranchUsers } from "../../../services/delivery";

const { Text } = Typography;
const { Option } = Select;

const ROLE_LABELS = {
  picker: "Picker",
  picking_verifier: "Picking Verifier",
  packing_verifier: "Packing Verifier",
  packer: "Packer",
  delivery_agent: "Delivery Agent",
  delivery_admin: "Delivery Admin",
};

const ROLE_COLORS = {
  picker: "blue",
  picking_verifier: "gold",
  packing_verifier: "cyan",
  packer: "purple",
  delivery_agent: "geekblue",
  delivery_admin: "red",
};

const DeliveryUsers = () => {
  const dispatch = useDispatch();
  const users = useSelector(selectDeliveryUsers);
  const loading = useSelector(selectDeliveryUsersLoading);

  const [modalOpen, setModalOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [branchUsers, setBranchUsers] = useState([]);
  const [branchUsersLoading, setBranchUsersLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchDeliveryUsers());
  }, [dispatch]);

  const openModal = async () => {
    setModalOpen(true);
    setBranchUsersLoading(true);
    try {
      const res = await getBranchUsers();
      const raw = res.data?.data || res.data?.message || [];
      setBranchUsers(Array.isArray(raw) ? raw : []);
    } catch {
      message.error("Failed to load users");
    } finally {
      setBranchUsersLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      setFormLoading(true);
      await addDeliveryUser({ user_id: values.user_id, role_type: values.role_type });
      message.success("Delivery role assigned");
      dispatch(fetchDeliveryUsers());
      closeModal();
    } catch (err) {
      if (err?.response?.data?.message) message.error(err.response.data.message);
      else if (err?.message && !err?.errorFields) message.error(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleRemove = async (userId, roleType) => {
    try {
      await removeDeliveryUser(userId, roleType);
      message.success("Role removed");
      dispatch(fetchDeliveryUsers());
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to remove role");
    }
  };

  // One row per user; parse roles into typed list
  const tableData = users.map((u) => {
    const roleList = (u.roles || "").split(",").map((r) => r.trim()).filter(Boolean).map((roleName) => {
      const type = Object.keys(ROLE_LABELS).find((t) => roleName.includes(t)) || roleName;
      return { type, roleName };
    });
    return { ...u, roleList, rowKey: u.id };
  });

  const columns = [
    {
      title: "Employee",
      dataIndex: "full_name",
      render: (val, record) => (
        <Space size={8} wrap>
          <Space size={4}>
            <UserOutlined style={{ color: "#888" }} />
            <Text strong>{val}</Text>
          </Space>
          {record.roleList.length > 0 ? (
            record.roleList.map(({ type, roleName }) => (
              <Tag
                key={type}
                color={ROLE_COLORS[type] || "default"}
                closable
                onClose={(e) => { e.preventDefault(); handleRemove(record.id, type); }}
              >
                {ROLE_LABELS[type] || roleName}
              </Tag>
            ))
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>No delivery role</Text>
          )}
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.setFieldsValue({ user_id: record.id });
            openModal();
          }}
        >
          Add Role
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
          Add Delivery User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tableData}
        loading={loading}
        rowKey={(r) => r.id}
        size="small"
        pagination={false}
        locale={{ emptyText: "No delivery users assigned yet" }}
      />

      <Modal
        title="Add Delivery User"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleAdd}
        confirmLoading={formLoading}
        okText="Assign Role"
        width={420}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="user_id"
            label="User"
            rules={[{ required: true, message: "Select a user" }]}
          >
            <Select
              showSearch
              placeholder="Select user"
              loading={branchUsersLoading}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {branchUsers.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.full_name || u.name || u.username}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="role_type"
            label="Delivery Role"
            rules={[{ required: true, message: "Select a role" }]}
          >
            <Select placeholder="Select role">
              {Object.entries(ROLE_LABELS).map(([type, label]) => (
                <Option key={type} value={type}>
                  <Tag color={ROLE_COLORS[type]}>{label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeliveryUsers;
